/**
 * Espelho tipado das coleções do Firestore que o painel lê.
 *
 * A fonte única do modelo é `zepadoca-functions/docs/06-banco-de-dados.md`.
 * Os aplicativos mantêm cópias próprias destes tipos em `domain/types/models.ts`,
 * e elas já divergem entre si: ao mexer aqui, confira lá.
 *
 * Quase tudo é opcional de propósito. O Firestore não tem esquema, os
 * documentos foram escritos por três aplicações ao longo do tempo, e um campo
 * marcado como obrigatório que chega ausente quebra a tela inteira em vez de
 * mostrar um traço.
 */

import type { DeliveryPricing } from "@/lib/sellerCheckout";

/** Data como ela pode chegar do Firestore. Use `toDate` de `lib/format`. */
export type FirestoreDate = unknown;

export interface GeoPointLike {
  latitude: number;
  longitude: number;
}

export interface SellerAddress {
  street?: string;
  number?: string;
  complement?: string;
  neighborhood?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  cep?: string;
  zip_code?: string;
}

export interface WorkingHours {
  weekDays?: string[];
  hours?: { start?: string; end?: string }[];
}

export interface HotBreadNotice {
  isActive?: boolean;
  messageType?: "freshBread" | "newProducts" | "custom";
  customText?: string;
  label?: string;
  displayDurationMinutes?: number;
  publishedAt?: FirestoreDate;
  expiresAt?: FirestoreDate;
  selectedProductIds?: string[];
}

export interface SellerStoriesInfo {
  activeCount?: number;
  lastPublishedAt?: FirestoreDate;
  latestExpiresAt?: FirestoreDate;
}

export interface Seller {
  id: string;
  name?: string;
  description?: string;
  email?: string;
  phone?: string;
  cnpj?: string;
  logo?: string;
  logoDisplayUrl?: string;
  logoThumbUrl?: string;
  logoBlurhash?: string;
  address?: SellerAddress;
  location?: GeoPointLike;
  geohash?: string;
  rating?: number;
  totalRatings?: number;
  isActive?: boolean;
  isOpen?: boolean;
  isTestSeller?: boolean;
  blockedAt?: FirestoreDate;
  blockReason?: string;
  unblockedAt?: FirestoreDate;
  estimatedDeliveryTimeInMinutes?: number;
  estimatedPickupTimeInMinutes?: number;
  deliveryFee?: number;
  minimumOrder?: number;
  deliveryOptions?: { pickup?: boolean; delivery?: boolean };
  /** O modo da taxa de entrega e a distância máxima, gravados pelo app do vendedor. */
  deliveryPricing?: DeliveryPricing | null;
  /** As formas de pagamento aceitas. Ausente: o cliente vê as quatro. */
  paymentMethods?: string[];
  workingHours?: WorkingHours[];
  /**
   * Legado: ids de tipos de produto que o estabelecimento usa. Organizava o
   * cardápio antes das seções; hoje só sustenta as versões antigas do app do
   * cliente.
   */
  categories?: string[];
  /** As seções do cardápio, criadas e ordenadas pelo vendedor. */
  menuSections?: MenuSection[];
  /**
   * A lista geral de adicionais: cada um com nome único, preço e status. Os
   * grupos e os produtos citam os ids (`addonIds`); mudar o preço ou o status
   * aqui vale em todos.
   */
  addons?: ProductAddon[];
  /** A versão da lista geral; abaixo de 1, o app do vendedor ainda migra os adicionais antigos. */
  addonCatalogVersion?: number;
  /** Os grupos de adicionais, que os produtos usam por referência (`addonGroupIds`). */
  addonGroups?: AddonGroup[];
  /** Os modelos de variações, que o cadastro do produto copia. */
  variationGroups?: VariationGroup[];
  sellsFrenchBread?: { isAvailable?: boolean; price?: number };
  hotBreadNotice?: HotBreadNotice;
  storiesInfo?: SellerStoriesInfo;
  searchKeywords?: string[];
  termsAcceptedAt?: FirestoreDate;
  createdAt?: FirestoreDate;
  updatedAt?: FirestoreDate;
}

/** Aba do cardápio a que a seção pertence. */
export const MENU_SECTION_KINDS = ["ready", "reserve"] as const;
export type MenuSectionKind = (typeof MENU_SECTION_KINDS)[number];

/**
 * Seção do cardápio, gravada pelo app do vendedor em `sellers.menuSections`.
 * A ordem do array é a ordem das seções (dentro de cada `kind`), e
 * `productIds` é a ordem dos produtos. Não tem relação com o tipo de produto.
 */
export interface MenuSection {
  id: string;
  name?: string;
  kind?: MenuSectionKind;
  productIds?: string[];
}

/**
 * Grupo de adicionais, em `sellers.addonGroups`. Entra inteiro nos produtos
 * que o ligam: desativar o grupo vale em todos. Os adicionais são os da lista
 * geral (`addonIds`); `addons` é uma cópia regravada a cada mudança, para as
 * versões antigas dos apps.
 */
export interface AddonGroup {
  id: string;
  name?: string;
  /** O mínimo que o cliente marca por item. Ausente ou null: sem mínimo. */
  minSelections?: number | null;
  /** Quantos o cliente marca por item. Ausente ou null: sem limite. */
  maxSelections?: number | null;
  /** Só `false` é gravado. */
  isAvailable?: boolean;
  /** Os adicionais do grupo, por id de `sellers.addons`. */
  addonIds?: string[];
  addons?: ProductAddon[];
}

/** Modelo de variações, em `sellers.variationGroups`. O produto copia as opções. */
export interface VariationGroup {
  id: string;
  name?: string;
  options?: { id: string; name: string; price?: number | null }[];
}

export interface SellerUser {
  id: string;
  name?: string;
  email?: string;
  phone?: string;
  sellerId?: string;
  onboardingCompleted?: boolean;
  isActive?: boolean;
  createdAt?: FirestoreDate;
  updatedAt?: FirestoreDate;
}

export interface UserAddress {
  id: string;
  nickname?: string;
  street?: string;
  number?: string;
  complement?: string;
  neighborhood?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  isDefault?: boolean;
  latitude?: number;
  longitude?: number;
}

export interface AppUser {
  id: string;
  name?: string;
  email?: string;
  phone?: string;
  cpf?: string;
  birthDate?: string;
  addresses?: UserAddress[];
  favoriteSellerIds?: string[];
  pushTokens?: { token?: string; platform?: string }[];
  userDeleted?: boolean;
  createdAt?: FirestoreDate;
  updatedAt?: FirestoreDate;
}

export const ORDER_STATUSES = [
  "pending",
  "weighing",
  "confirmed",
  "preparing",
  "ready",
  "delivering",
  "delivered",
  "cancelled",
] as const;

export type OrderStatus = (typeof ORDER_STATUSES)[number];

/** Os estados em que o pedido ainda está acontecendo. */
export const OPEN_ORDER_STATUSES: OrderStatus[] = [
  "pending",
  "weighing",
  "confirmed",
  "preparing",
  "ready",
  "delivering",
];

export interface OrderItem {
  productId?: string;
  productName?: string;
  price?: number;
  quantity?: number;
  note?: string;
  isReserveItem?: boolean;
  saleType?: "unit" | "kilo";
  saleFormat?: string;
  weightInGrams?: number;
  isWeighed?: boolean;
  estimatedPrice?: number;
  /** Separa duas linhas do mesmo produto (variação e adicionais diferentes). */
  lineId?: string;
  /**
   * A variação escolhida. O título do item é "variation.productName -
   * variation.name"; `productName` traz esse título nos pedidos de agora e só
   * o nome da variação nos de antes. `image` só vem quando a variação tem foto
   * própria.
   */
  variation?: { id: string; name: string; productName: string; price: number; image?: string | null };
  /** Os adicionais marcados, fora de `price`: somam `addonsUnitPrice` por unidade. */
  addons?: { id: string; name: string; price: number }[];
  addonsUnitPrice?: number;
  /** No combo, o retrato de cada item com a escolha do cliente. */
  compositeItems?: {
    id: string;
    kind: CompositeItem["kind"];
    name: string;
    quantity?: number | null;
    choiceId?: string | null;
    /** No item de escolha com mais de uma opção, os ids escolhidos (`choiceName` junta os nomes). */
    choiceIds?: string[] | null;
    choiceName?: string | null;
    productId?: string | null;
  }[];
}

export interface Order {
  id: string;
  userId?: string;
  sellerId?: string;
  sellerName?: string;
  userName?: string;
  userPhone?: string;
  status?: OrderStatus;
  /** O motivo que o vendedor escreveu ao cancelar. */
  statusReason?: string;
  statusHistory?: { status?: OrderStatus; timestamp?: FirestoreDate; reason?: string }[];
  items?: OrderItem[];
  subtotal?: number;
  deliveryFee?: number;
  total?: number;
  totalAmount?: number;
  deliveryType?: "delivery" | "pickup";
  delivery?: UserAddress & { instructions?: string };
  paymentMethod?: string;
  appliedCoupon?: {
    code?: string;
    discount?: number;
    discountType?: string;
    description?: string;
    couponId?: string;
    sponsoredBy?: "platform" | "seller";
  } | null;
  needsWeighing?: boolean;
  /** Não é gravado na prática: o código do pedido sai do id (`lib/orderCode.ts`). */
  orderNumber?: string;
  userDeleted?: boolean;
  rating?: { value?: number; comment?: string };
  createdAt?: FirestoreDate;
  updatedAt?: FirestoreDate;
}

/**
 * Tipo de produto. A coleção continua `categories`, e a interface já o chamou
 * de categoria. O tipo padrão, "Outros", não existe aqui: ver
 * `lib/productTypes.ts`.
 */
export interface Category {
  id: string;
  name?: string;
  slug?: string;
  icon?: string;
  imageUrl?: string;
  order?: number;
  isActive?: boolean;
  hasProducts?: boolean;
  productsCount?: number;
  description?: string;
  /** Resíduo de um desenho anterior: a coleção é global. */
  sellerId?: string;
}

export interface Product {
  id: string;
  sellerId?: string;
  name?: string;
  description?: string;
  price?: number;
  /** Tipo de produto: id de `categories`, ou "outros" (o padrão). */
  category?: string;
  image?: string;
  imageThumbUrl?: string;
  imageDisplayUrl?: string;
  isAvailable?: boolean;
  isReserveItem?: boolean;
  preparationTime?: number;
  /** Unidade de `preparationTime`. Ausente vale "days". */
  preparationTimeUnit?: "days" | "hours";
  saleType?: "unit" | "kilo";
  rating?: number;
  /**
   * Variações do produto. Com elas, o produto não é opção: cada variação tem o
   * próprio preço, e `price` é o da variação padrão. Sem preço (dado antigo),
   * vale o do produto.
   */
  variations?: ProductVariation[];
  /** A variação que vem marcada para o cliente. Ausente ou removida: a primeira. */
  defaultVariationId?: string | null;
  /** A lista comum de adicionais, por id de `sellers.addons` (sem a lista por variação). */
  addonIds?: string[];
  /** Legado: a cópia dos adicionais, de antes da lista geral. Os produtos novos gravam vazia. */
  addons?: ProductAddon[];
  addonsPerVariation?: boolean;
  /** Os grupos de `sellers.addonGroups` ligados ao produto. */
  addonGroupIds?: string[];
  /** O mínimo de adicionais que o cliente marca por item. Ausente ou null: sem mínimo. */
  minAddons?: number | null;
  /** Quantos adicionais o cliente marca por item. Ausente ou null: sem limite. */
  maxAddons?: number | null;
  /**
   * Produto composto (combo): preço próprio e uma lista de itens, sempre por
   * unidade. Com `compositeItemsPerVariation`, cada variação tem a própria lista.
   */
  isComposite?: boolean;
  compositeItems?: CompositeItem[];
  compositeItemsPerVariation?: boolean;
  /** Marcado no switch "É pão francês?" do cadastro. Ausente nos produtos antigos. */
  isFrenchBread?: boolean;
  createdAt?: FirestoreDate;
}

/** Um adicional que o cliente marca. Sem preço (ou 0) é grátis. */
export interface ProductAddon {
  id: string;
  name: string;
  price?: number | null;
  /** Só `false` é gravado: desativado, sem sair do cadastro. */
  isAvailable?: boolean;
}

export interface ProductVariation {
  id: string;
  name: string;
  price?: number | null;
  description?: string | null;
  image?: string | null;
  imageThumbUrl?: string | null;
  imageDisplayUrl?: string | null;
  /** Só `false` é gravado: o cliente não vê a variação. */
  isAvailable?: boolean;
  /** Os adicionais da variação, por id de `sellers.addons`; `addons` é o legado. */
  addonIds?: string[];
  addons?: ProductAddon[];
  addonGroupIds?: string[];
  /** Os itens do combo nesta variação, com `Product.compositeItemsPerVariation`. */
  compositeItems?: CompositeItem[];
}

/**
 * Um item do combo: simples ("2 pães de queijo"), de escolha (o cliente
 * escolhe exatamente `choiceCount` opções, 1 quando ausente) ou um produto do
 * cardápio, com qualquer variação ou uma fixa. `productName` e
 * `variationName` são cópias para exibir.
 */
export type CompositeItem =
  | { id: string; kind: "simple"; name: string; quantity?: number | null }
  | {
      id: string;
      kind: "choice";
      name: string;
      quantity?: number | null;
      options: { id: string; name: string }[];
      choiceCount?: number | null;
    }
  | {
      id: string;
      kind: "product";
      productId: string;
      productName: string;
      quantity?: number | null;
      variationMode: "any" | "fixed";
      variationId?: string | null;
      variationName?: string | null;
    };

export interface Coupon {
  id: string;
  code?: string;
  description?: string;
  discount?: number;
  /**
   * "free_shipping" nasce no app do vendedor: zera a taxa de entrega do
   * pedido, não tem valor (`discount` 0) e é sempre do estabelecimento.
   */
  discountType?: "percentage" | "fixed" | "free_shipping";
  /** Frete grátis: "order" vale no pedido que aplicar o cupom, "products" na compra dos produtos listados. */
  freeShippingScope?: "order" | "products";
  /** Frete grátis por produto: ids de `products` do estabelecimento. */
  freeShippingProductIds?: string[];
  minValue?: number;
  maxDiscount?: number;
  expirationDate?: FirestoreDate;
  isActive?: boolean;
  isVisibleAtCheckout?: boolean;
  establishment?: string;
  fromSellers?: string[];
  /** É um array na prática, apesar de os apps o tiparem como string. */
  userIds?: string[] | string;
  usageLimit?: number;
  usageCount?: number;
  /**
   * Quem paga o desconto. "platform" é promoção do Zé Padoca: o desconto volta
   * ao estabelecimento como crédito na fatura. Ausente vale "seller".
   */
  sponsoredBy?: "platform" | "seller";
  userId?: string;
  favoritesNotifiedAt?: FirestoreDate;
  createdAt?: FirestoreDate;
  updatedAt?: FirestoreDate;
}

export interface SellerRating {
  id: string;
  userId?: string;
  sellerId?: string;
  isGuest?: boolean;
  rating?: number;
  comment?: string;
  createdAt?: FirestoreDate;
}

export interface Subscription {
  id: string;
  userId?: string;
  sellerId?: string;
  status?: "pending" | "active" | "cancelled";
  deliverySchedule?: { weekDays?: string[]; time?: string };
  quantity?: number;
  price?: number;
  paymentStatus?: "pending" | "completed";
  createdAt?: FirestoreDate;
}

export const BILLING_STATUSES = [
  "Active",
  "PendingPayment",
  "Expired",
  "Canceled",
  "Inactive",
  "Blocked",
] as const;

export type BillingStatusValue = (typeof BILLING_STATUSES)[number];

export interface SellerBillingStatus {
  id: string;
  sellerId?: string;
  sellerName?: string;
  status?: BillingStatusValue;
  dueDay?: number;
  activatedAt?: FirestoreDate;
  canceledAt?: FirestoreDate;
  expiredAt?: FirestoreDate;
  oldestOverdueMonth?: { month?: number; year?: number };
  overdueAmount?: number;
  selfHealed?: boolean;
  updatedAt?: FirestoreDate;
  /**
   * Parceiro de teste: as faturas a partir de `testPartnerSince` nascem
   * ignoradas, sem a comissão do Zé Padoca. Escrito só pelo backend
   * (adminSetTestPartner). Não confundir com `Seller.isTestSeller`, que é o
   * pagamento de teste do Mercado Pago.
   */
  isTestPartner?: boolean;
  testPartnerSince?: { month?: number; year?: number };
  testPartnerSetAt?: FirestoreDate;
  testPartnerSetBy?: string;
  testPartnerEndedAt?: FirestoreDate;
  testPartnerEndedBy?: string;
}

export type BillingWaiverSource = "manual" | "testPartner";

export interface SellerBilling {
  id: string;
  sellerId?: string;
  sellerName?: string;
  month?: number;
  year?: number;
  totalRevenue?: number;
  platformFee?: number;
  platformFeePercentage?: number;
  /** Comissão antes do crédito de cupons do Zé Padoca. */
  platformFeeGross?: number;
  /** Descontos de cupons da plataforma devolvidos ao vendedor no mês. */
  platformCouponCredit?: number;
  platformCouponOrdersCount?: number;
  /** Crédito que passou da comissão do mês: a plataforma repassa ao vendedor. */
  platformCouponCreditExcess?: number;
  deliveredOrdersCount?: number;
  paid?: boolean;
  paidAt?: FirestoreDate;
  paidViaBillingId?: string;
  calculatedAt?: FirestoreDate;
  dueDate?: FirestoreDate;
  expiredDebt?: number;
  expiredDebtItems?: { month?: number; year?: number; platformFee?: number; billingId?: string }[];
  totalAmount?: number;
  accumulatedIntoBillingId?: string;
  pendingPayment?: {
    method?: "pix" | "boleto";
    status?: "pending" | "paid" | "failed" | "cancelled" | "expired";
    totalAmount?: number;
    createdAt?: FirestoreDate;
    expiresAt?: FirestoreDate;
  };
  lastEmailSentAt?: FirestoreDate;
  lastEmailSentTo?: string;
  /** Baixa manual pelo painel. Não existe em fatura quitada pelo Mercado Pago. */
  paidManually?: boolean;
  paidManuallyBy?: string;
  paidManuallyReason?: string;
  /** Estorno pelo painel. */
  reopenedAt?: FirestoreDate;
  reopenedBy?: string;
  reopenedReason?: string;
  /**
   * Fatura ignorada: a comissão do mês foi dispensada (`platformFee` zerado,
   * o valor em `waivedPlatformFee`). Sem dívida acumulada a pagar, ela vale
   * como paga; com dívida, segue em aberto só por ela.
   */
  waived?: boolean;
  waivedSource?: BillingWaiverSource;
  waivedAt?: FirestoreDate;
  /** UID de quem ignorou, ou "testPartner" quando nasceu ignorada. */
  waivedBy?: string;
  waivedReason?: string;
  /** Ignorada junto com a fatura que a acumulava. */
  waivedViaBillingId?: string;
  waivedPlatformFee?: number;
  /** Voltou a ser cobrada pelo painel. */
  unwaivedAt?: FirestoreDate;
  unwaivedBy?: string;
  unwaivedReason?: string;
}

export const NOTIFICATION_AUDIENCES = ["clients", "sellers", "custom"] as const;

export type NotificationAudience = (typeof NOTIFICATION_AUDIENCES)[number];

/**
 * Histórico dos envios dirigidos, escrito por `adminSendNotification`.
 * Nenhum aplicativo toca nesta coleção.
 */
export interface AdminNotification {
  id: string;
  audience?: NotificationAudience;
  title?: string;
  body?: string;
  data?: Record<string, string>;
  targetCount?: number;
  recipients?: number;
  withoutToken?: number;
  devices?: number;
  successCount?: number;
  failureCount?: number;
  /** Preenchido quando o envio falhou. O documento é gravado mesmo assim. */
  error?: string | null;
  sentBy?: string;
  sentAt?: FirestoreDate;
}

export interface AppConfig {
  clientMinVersion?: string;
  sellerMinVersion?: string;
}

export interface BillingConfig {
  platformFeePercentage?: number;
  createdAt?: FirestoreDate;
  updatedAt?: FirestoreDate;
}
